import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, PropType, ref, watch } from 'vue';
import { ChecklistDropdownField } from '@/interfaces/components/configurator/ChecklistDropdownField';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import {
    extractAndRunOptionsFunction,
    mapVisibleOptionVIDsToChecklistDropdownOptions,
} from '@/helpers/FieldFunctionHelper';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { ProductDetails } from '@/interfaces/components/configurator/ProductDetails';
import { EventBus } from '@/helpers/EventBusHelper';
import { defaultValueWarnings as defaultValueWarningsConfig } from '@/interfaces/components/projectNew/DefaultValueWarnings';
import { GlobalOptions } from '@/enums/global/GlobalOptions';
import {
    allowedColorModuleProducts,
    allowedColorModuleProductSubGroups,
    allowedColorModuleProductGroups,
} from '@/regex/allowedColorModuleEntities';
import { checklistFieldsPIDsSet } from '@/regex/validChecklistFieldsForColorModule';
import store from '@/store';
import CustomSelect from '@/components/views/productView/CustomSelect.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ChecklistDropdownFieldModule',
  props: {
    checklistDropdownField: {
        type: Object as PropType<ChecklistDropdownField>,
        required: true,
    },
    pId: { type: String, required: true },
    itemRowIndex: { type: Number, default: 999 },
    isDisabled: { type: Boolean, default: false },
    image: { required: true },
    debounceToggleIsActive: {
        type: Function as PropType<(state: boolean) => void>,
        default: null,
    },
    allowAllDropdownOptions: { type: Boolean, default: false },
    activeProductFormId: { type: Number, default: null },
    isConfigurator: { type: Boolean, default: false },
    productDetails: {
        type: Object as PropType<ProductDetails | null>,
        default: null,
    },
    checklistSegmentName: { type: String },
},
  emits: ['updateStoreFieldValue'],
  setup(__props, { expose, emit }) {

const props = __props;





const value = ref<string | null>('#');
const visibleOptionVIDs = ref<string[]>([]);
const globalOptions = GlobalOptions;
const svgSource = require('../../../../public/color-picker.svg');
const availableColorModuleProducts = allowedColorModuleProducts;

const visibleDropdownOptions = computed(() => {
    if (visibleOptionVIDs.value.length <= 0) {
        return props.checklistDropdownField.checklistDropdownOptions;
    }

    return mapVisibleOptionVIDsToChecklistDropdownOptions(
        visibleOptionVIDs.value,
        props.checklistDropdownField.checklistDropdownOptions,
    );
});

const defaultValueWarnings = computed(() => {
    const warnings = store.getters['configurator/defaultValueWarnings'];

    if (warnings.length) {
        return warnings.find(
            (warning: defaultValueWarningsConfig) => warning.productFormId === props.activeProductFormId,
        );
    }

    return null;
});

const fieldValue = computed(() => {
    let currentValue: string | null = '';
    if (props.activeProductFormId == null) {
        return '';
    }

    currentValue = store.getters['configurator/activeProductFormValue'](props.pId, props.activeProductFormId) as
        | string
        | null;

    return currentValue;
});

const shouldShowAsButtons = computed(() => visibleDropdownOptions.value.length <= 3);

const isRoltekUserGroup = computed((): boolean => store.getters['jwtData/isRoltekUserGroup']);

const isColorModuleEnabled = computed((): boolean => {
    if (!checklistFieldsPIDsSet.has(props.pId) || props.productDetails == null) {
        return false;
    }

    const productCategory = props.productDetails?.productCategory;
    const productName = props.productDetails?.productName;
    const productSystemName = props.productDetails?.productSystemName;

    if (productCategory && allowedColorModuleProductGroups.length > 0) {
        for (const group of allowedColorModuleProductGroups) {
            if (productCategory.toLowerCase().includes((group as string).toLowerCase())) {
                return true;
            }
        }
    }

    if (productCategory && allowedColorModuleProductSubGroups.length > 0) {
        for (const subGroup of allowedColorModuleProductSubGroups) {
            if (productCategory.toLowerCase().includes((subGroup as string).toLowerCase())) {
                return true;
            }
        }
    }

    if (productName && allowedColorModuleProducts.length > 0) {
        for (const product of allowedColorModuleProducts) {
            if (productName.toLowerCase().includes((product as string).toLowerCase())) {
                if (
                    productName === 'KARO' &&
                    (props.checklistSegmentName === 'Omarica' || props.checklistSegmentName === 'Vodila')
                ) {
                    return false;
                }
                return true;
            }

            if (productSystemName === product) {
                return true;
            }
        }
    }

    return false;
});

const areNewCMChangesEnabled = computed(
    () => isColorModuleEnabled.value && availableColorModuleProducts.includes(props.productDetails?.productSystemName!),
);

const oldCMActive = computed(() => false);

function runVisibleOptionVIDs({
    activeProductFormValues,
    activeProductFormId,
}: {
    activeProductFormValues: ActiveProductFormValueObject;
    activeProductFormId: number;
}) {
    if (activeProductFormId !== props.activeProductFormId) {
        return;
    }

    if (
        props.checklistDropdownField.optionsFunction == null ||
        props.checklistDropdownField.optionsFunctionData == null ||
        props.allowAllDropdownOptions
    ) {
        visibleOptionVIDs.value = [];
        return;
    }

    visibleOptionVIDs.value = extractAndRunOptionsFunction(
        props.checklistDropdownField.optionsFunction,
        activeProductFormValues,
        props.checklistDropdownField.optionsFunctionData,
    ).split(';');

    if (fieldValue.value == null || visibleOptionVIDs.value.includes(fieldValue.value)) {
        return;
    }

    onChange(visibleOptionVIDs.value[0]);
}

function onSelect() {
    EventBus.$emit(EventBusEvents.changesInDataMade, { state: true });
    window.setTimeout(() => {
        props.debounceToggleIsActive?.(true);
    }, 200);
}

function onCustomDropdownSelect(selectedValue: string) {
    value.value = selectedValue;
    onChange(selectedValue);
    onSelect();
}

function onChange(checked: string) {
    emit(EventBusEvents.updateStoreFieldValue, {
        pId: props.pId,
        value: checked,
        productFormId: props.activeProductFormId,
    });
}

function onBlur() {
    props.debounceToggleIsActive?.(false);
}

function onFocus() {
    window.setTimeout(() => {
        props.debounceToggleIsActive?.(true);
    }, 200);
}

function updateColorValue(payload: {
    activeProductFormId: number;
    colorSystemPID: string;
    colorCollection: string;
    colorDropdownPID: string;
    selectedColorValue: string | null;
    shouldFreezeManualInput?: boolean;
}) {
    if (payload.colorSystemPID !== props.pId) {
        return;
    }

    value.value = payload.colorCollection;
    onChange(payload.colorCollection);

    if (payload.selectedColorValue) {
        nextTick(() => {
            emit(EventBusEvents.updateStoreFieldValue, {
                pId: payload.colorDropdownPID,
                value: payload.selectedColorValue as string,
            });
        });

        EventBus.$emit(EventBusEvents.updateDisabledFieldsInMultiposition, payload);
    }
}

function openColorModule(payload: {
    colorSystemPID: string;
    fieldValue: GlobalOptions;
    activeProductFormId: number | null;
    productName?: string;
    productCategory?: string;
}) {
    EventBus.$emit(EventBusEvents.showColorModule, payload);
}

EventBus.$on(EventBusEvents.triggerOptionsFunctions, runVisibleOptionVIDs);

if (props.activeProductFormId === null) {
    EventBus.$on(EventBusEvents.updateMultipositionHeaderSystemColorValue, updateColorValue);
}

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.triggerOptionsFunctions, runVisibleOptionVIDs);

    if (props.activeProductFormId === null) {
        EventBus.$off(EventBusEvents.updateMultipositionHeaderSystemColorValue, updateColorValue);
    }
});

watch(
    fieldValue,
    (newValue: string | null | undefined, oldValue: string | null | undefined) => {
        if (newValue === oldValue) {
            return;
        }

        value.value = newValue ?? '';
    },
    { immediate: true },
);

expose({ runVisibleOptionVIDs });

return { __sfc: true,props, emit, value, visibleOptionVIDs, globalOptions, svgSource, availableColorModuleProducts, visibleDropdownOptions, defaultValueWarnings, fieldValue, shouldShowAsButtons, isRoltekUserGroup, isColorModuleEnabled, areNewCMChangesEnabled, oldCMActive, runVisibleOptionVIDs, onSelect, onCustomDropdownSelect, onChange, onBlur, onFocus, updateColorValue, openColorModule, CustomSelect }
}

})